import { memo, useState, useSyncExternalStore } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { ORPCError } from '@orpc/client'
import { Mail, MailX, Paperclip } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ChatEmailRef } from '@hub/contracts'
import { orpc } from '@/lib/orpc-client'
import { field, mono } from '@/components/assistant-ui/elements/surfaces'
import { cn } from '@/lib/utils'
import { formatDayDate, formatTime } from './time'

/**
 * The e-mail an inbox run handed to HUB (board cik7), shown in the thread as
 * an envelope instead of the whole mail: sender, subject, received time and
 * the attachment count. Clicking opens the mail in the inbox.
 *
 * Whether the mail still exists is checked lazily on click (one `getEmail`
 * per click, never per message): a deleted mail flips every card for it in
 * this tab to a muted "deleted" state.
 */

// ── deleted e-mails seen by this tab ─────────────────────────────────────────

const goneEmails = new Set<string>()
const listeners = new Set<() => void>()

function markGone(emailId: string) {
  goneEmails.add(emailId)
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

function useEmailGone(emailId: string): boolean {
  return useSyncExternalStore(
    subscribe,
    () => goneEmails.has(emailId),
    () => goneEmails.has(emailId)
  )
}

/** Test hook: forget every deleted id (module state survives between renders). */
export function resetGoneEmails() {
  goneEmails.clear()
}

// ── card ─────────────────────────────────────────────────────────────────────

interface EmailEnvelopeProps {
  email: ChatEmailRef
  projectId: string
}

function EmailEnvelopeImpl({ email, projectId }: EmailEnvelopeProps) {
  const { t } = useTranslation('chat')
  const navigate = useNavigate()
  const gone = useEmailGone(email.emailId)
  const [checking, setChecking] = useState(false)

  const open = async () => {
    if (checking) return
    setChecking(true)
    try {
      const { email: live } = await orpc.inbox.getEmail({ projectId, emailId: email.emailId })
      await navigate({
        to: '/$projectId/inbox',
        params: { projectId },
        search: {
          email: email.emailId,
          folder: live.folder === 'inbox' ? undefined : live.folder,
        },
      })
    } catch (err) {
      if (err instanceof ORPCError && err.code === 'NOT_FOUND') markGone(email.emailId)
      else throw err
    } finally {
      setChecking(false)
    }
  }

  const from = email.fromName?.trim() || email.fromAddress
  const subject = email.subject?.trim() || t('email.noSubject')
  const received = `${formatDayDate(email.receivedAt)} ${formatTime(email.receivedAt)}`.trim()

  if (gone) {
    return (
      <div
        data-slot="email-envelope"
        data-state="deleted"
        className="border-border text-muted-foreground flex max-w-sm items-center gap-2.5 rounded-2xl border border-dashed px-3.5 py-2.5 text-sm"
      >
        <MailX className="size-4 shrink-0 opacity-60" aria-hidden />
        <span className="italic">{t('email.deleted')}</span>
      </div>
    )
  }

  return (
    <button
      type="button"
      data-slot="email-envelope"
      onClick={() => void open()}
      aria-busy={checking || undefined}
      aria-label={t('email.open')}
      title={t('email.open')}
      className={cn(
        field,
        'hover:bg-foreground/[0.08] flex w-full max-w-sm cursor-pointer items-start gap-3 rounded-2xl px-3.5 py-2.5 text-left transition-colors'
      )}
    >
      <span className="bg-accent-tint text-accent-tint-foreground mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full">
        <Mail className="size-4" aria-hidden />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="flex items-baseline gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium" data-field="from">
            {from}
          </span>
          <span
            className={cn(mono, 'text-foreground/40 shrink-0 tabular-nums')}
            data-field="received"
          >
            {received}
          </span>
        </span>
        {email.fromName?.trim() && (
          <span className="text-foreground/50 truncate text-xs" data-field="address">
            {email.fromAddress}
          </span>
        )}
        <span className="truncate text-sm" data-field="subject">
          {subject}
        </span>
        {email.attachmentCount > 0 && (
          <span
            className="text-foreground/50 flex items-center gap-1 text-xs"
            data-field="attachments"
          >
            <Paperclip className="size-3" aria-hidden />
            {t('email.attachments', { count: email.attachmentCount })}
          </span>
        )}
      </span>
    </button>
  )
}

export const EmailEnvelope = memo(EmailEnvelopeImpl)
